import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";

type Size = "lg" | "sm";

/** Copies the exact command; announces via aria-live; falls back to selecting the text when the Clipboard API is blocked. */
export function CopyCommandButton({ text, testId, size = "lg" }: { text: string; testId: string; size?: Size }) {
  const [state, setState] = useState<"idle" | "copied" | "manual">("idle");
  const [msg, setMsg] = useState("");
  const codeRef = useRef<HTMLElement>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  useEffect(() => {
    if (state !== "manual" || !codeRef.current) return;
    const r = document.createRange();
    r.selectNodeContents(codeRef.current);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(r);
  }, [state]);
  const legacyCopy = () => {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.cssText = "position:fixed;opacity:0;top:0;left:0";
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand("copy"); } catch { ok = false; }
    ta.remove();
    return ok;
  };
  const copy = async () => {
    let ok = false;
    try { if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); ok = true; } } catch { ok = false; }
    if (!ok) ok = legacyCopy();
    window.clearTimeout(timer.current);
    if (ok) {
      setState("copied");
      setMsg(`Copied ${text}`);
      timer.current = window.setTimeout(() => { setState("idle"); setMsg(""); }, 2000);
    } else {
      setState("manual");
      setMsg(`Copy not available. ${text} is selected; copy it manually.`);
    }
  };
  const big = size === "lg";
  return (
    <div className={big ? "w-full" : ""}>
      <button type="button" onClick={copy} data-testid={testId} aria-label={`Copy command ${text}`}
        className={`j-press inline-flex items-center justify-center gap-2 border-[3px] font-condensed font-extrabold uppercase tracking-[0.08em] transition-colors ${big
          ? "min-h-[56px] w-full border-[#F5D536] bg-[#F5D536] px-6 text-lg text-[#171A1F] hover:bg-[#FFE45C]"
          : "min-h-[44px] border-[#F5D536] px-4 text-sm text-[#F5D536] hover:bg-[#F5D536] hover:text-[#171A1F]"}`}>
        {state === "copied" ? <Check className="h-5 w-5" aria-hidden /> : <Copy className="h-5 w-5" aria-hidden />}
        {state === "copied" ? "Copied" : "Copy command"}
      </button>
      {state === "manual" && (
        <p className="mt-2 text-sm text-[#FFFFFF]" data-testid={`${testId}-manual`}>
          Press and hold (or Ctrl/Cmd+C) to copy: <code ref={codeRef} className="select-all bg-[#FFFFFF] px-1.5 py-0.5 font-condensed text-base font-extrabold text-[#171A1F]">{text}</code>
        </p>
      )}
      <span className="sr-only" aria-live="polite" data-testid={`${testId}-live`}>{msg}</span>
    </div>
  );
}
